// @flow
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import Debug from 'debug';
import { FormattedMessage } from 'react-intl';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Fab from '@material-ui/core/Fab';
import Drawer from '@material-ui/core/Drawer';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import PlaylistPlayIcon from '@material-ui/icons/PlaylistPlay';
import PlayIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import CloseIcon from '@material-ui/icons/Close';
import LoopIcon from '@material-ui/icons/Loop';
import Config from '../../models/Config';
import environmentLoader from '../../utils/environmentLoader';
import { configSelector } from './selectors';
import FilePlayer from './FilePlayer';
import Generator from './Generator';

const debug = Debug('amms:modules:tisPlayer');

const useStyles = makeStyles(theme => ({
    drawer: {
        // position: 'relative'
    },
    drawerOpen: {
        height: props => (props.playerType === 'generator' ? '70vh' : 240),
        transition: theme.transitions.create('height', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        })
    },
    drawerClose: {
        transition: theme.transitions.create('height', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        }),
        height: 0
    },
    drawerPaper: {
        overflowY: 'visible'
    },
    controls: {
        position: 'absolute',
        top: -53,
        right: 0
    },
    controlsButtons: {
        float: 'right'
    },
    controlsButton: {
        marginRight: 5
    }
}));

function TisPlayer() {
    const config = useSelector(configSelector);
    const [playerType, setPlayerType] = useState(config.playerType || 'filePlayer');
    const classes = useStyles({ playerType });
    const [currentConfig, setCurrentConfig] = useState(config || {});
    const [socket, setSocket] = useState();
    const [connected, setConnected] = useState(false);
    const [playerVisible, setPlayerVisible] = useState(false);
    const playerRef = useRef();
    const [isPlaying, setIsPlaying] = useState(config.autoplay);
    const [loopEnabled, setLoopEnabled] = useState(config.loop);

    const getPlayerRef = (ref) => {
        if(ref && !playerRef.current && config.autoplay) {
            ref.play();
        }

        playerRef.current = ref;
    };

    useEffect(() => setCurrentConfig(config || {}), [config]);

    useEffect(() => {
        debug('Connecting socket');

        const io = require('socket.io-client'); // eslint-disable-line global-require

        const config = environmentLoader.load('config');
        if(!config) {
            throw new Error('Config is undefined');
        }
        let url;
        if(config.socketPort) {
            url = `${window.location.protocol}//${window.location.hostname}:${config.socketPort}`;
        }

        const _socket = io(url, { path: '/tis' });
        setSocket(_socket);

        _socket.on('connect', () => {
            debug('Socket connected');

            setConnected(true);
        });
        _socket.on('disconnect', () => {
            debug('Socket disconnected');

            setConnected(false);
        });
        _socket.on('error', (e) => {
            console.error('Socket error', e);

            setConnected(false);
        });

        return () => {
            debug('Disconnecting socket');
            _socket.disconnect();
        };
    }, []);

    useEffect(() => setPlayerType(config.playerType || 'filePlayer'), [config.playerType]);

    useEffect(() => {
        if(config.autoplay && playerRef.current && !playerRef.current.isPlaying) {
            playerRef.current.play();
        }
    }, [config.autoplay]);

    useEffect(() => setLoopEnabled(config.loop), [config.loop]);

    const showPlayer = () => setPlayerVisible(true);
    const hidePlayer = () => setPlayerVisible(false);

    const togglePlay = () => {
        if(playerRef.current && playerRef.current.isPlaying) {
            playerRef.current.pause();
        }
        else if(playerRef.current && !playerRef.current.isPlaying) {
            playerRef.current.play();
        }
    };
    const toggleLoop = () => {
        const newConfig = {
            ...config,
            loop: !loopEnabled
        };

        Config.findByIdAndUpdate('tisPlayer', newConfig, { upsert: true });
    };

    const playMessage = useCallback((message) => {
        if(socket) {
            // debug('Playing message', message);
            message.TimeOfTrackInformation = new Date();
            socket.emit('tis', JSON.stringify(message));
        }
        else {
            console.error('Cant play message. Socket is not connected');
        }
    }, [socket]);

    const handlePlay = useCallback(() => setIsPlaying(true), []);
    const handleStop = useCallback(() => setIsPlaying(false), []);

    const handlePlayerTypeChange = (e, newValue) => {
        setPlayerType(newValue);

        const newConfig = {
            ...config,
            playerType: newValue
        };

        Config.findByIdAndUpdate('tisPlayer', newConfig, { upsert: true });
    };

    const handleConfigChange = (newConfig) => {
        console.log('newConfig: ', newConfig);
        setCurrentConfig(newConfig);

        Config.findByIdAndUpdate('tisPlayer', newConfig, { upsert: true });
    };

    return (
        <React.Fragment>
            <Drawer
                className={classnames(classes.drawer, {
                    [classes.drawerOpen]: playerVisible,
                    [classes.drawerClose]: !playerVisible
                })}
                variant="persistent"
                anchor="bottom"
                open={true}
                classes={{
                    paper: classnames(classes.drawerPaper, {
                        [classes.drawerOpen]: playerVisible,
                        [classes.drawerClose]: !playerVisible
                    })
                }}
                onClose={hidePlayer}
            >
                <div className={classes.controls}>
                    <div className={classes.controlsButtons}>
                        {playerType === 'filePlayer' &&
                            <Fab
                                className={classes.controlsButton}
                                color={loopEnabled ? 'secondary' : undefined}
                                size="small"
                                aria-label="Loop"
                                onClick={toggleLoop}
                            >
                                <LoopIcon />
                            </Fab>
                        }
                        <Fab
                            className={classes.controlsButton}
                            color="secondary"
                            size="small"
                            aria-label="Play TIS track"
                            onClick={togglePlay}
                        >
                            {isPlaying ?
                                <PauseIcon/>
                                :
                                <PlayIcon/>
                            }
                        </Fab>

                        {!playerVisible &&
                            <Fab
                                className={classes.controlsButton}
                                color="secondary"
                                size="small"
                                aria-label="show TIS Player"
                                onClick={showPlayer}
                            >
                                <PlaylistPlayIcon />
                            </Fab>
                        }
                        {playerVisible &&
                            <Fab
                                className={classes.controlsButton}
                                size="small"
                                onClick={hidePlayer}
                            >
                                <CloseIcon/>
                            </Fab>
                        }
                    </div>
                </div>


                <Tabs value={playerType} onChange={handlePlayerTypeChange}>
                    <Tab
                        label={(
                            <FormattedMessage
                                id="tisPlayer.filePlayer"
                                defaultMessage="File Player"
                            />
                        )}
                        value="filePlayer"
                    />
                    <Tab
                        label={(
                            <FormattedMessage
                                id="tisPlayer.generator"
                                defaultMessage="Generator"
                            />
                        )}
                        value="generator"
                    />
                </Tabs>

                {playerType === 'filePlayer' &&
                    <FilePlayer
                        playerRef={getPlayerRef}
                        connected={connected}
                        config={currentConfig}
                        loopEnabled={loopEnabled}
                        playMessage={playMessage}
                        onPlay={handlePlay}
                        onStop={handleStop}
                    />
                }
                {playerType === 'generator' &&
                    <Generator
                        playerRef={getPlayerRef}
                        connected={connected}
                        config={currentConfig}
                        onConfigChange={handleConfigChange}
                        playMessage={playMessage}
                        onPlay={handlePlay}
                        onStop={handleStop}
                    />
                }
            </Drawer>
        </React.Fragment>
    );
}

export default TisPlayer;